import { CONSENT_STORAGE_KEY, inquirySourceAllowed, onConsentChange } from "@/lib/consent";

const UNKNOWN = "unknown";
export const INQUIRY_SOURCE_KEY = "chleb:inquiry-source:v1";
export const INQUIRY_SOURCE_TTL_MS = 30 * 60 * 1000;
const MAX_PATH_LENGTH = 300;
const MAX_LABEL_LENGTH = 80;
const MAX_HOST_LENGTH = 253;

export interface LeadAttributionContext {
  entryPagePath: string;
  pagePath: string;
  referrerHost: string;
  utm: {
    source: string;
    medium: string;
    campaign: string;
  };
}

export interface BrowserAttributionSource {
  pathname: string;
  search: string;
  referrer: string;
  origin?: string;
  navigationType?: string;
}

interface EntrySnapshot {
  version: 1;
  createdAt: number;
  expiresAt: number;
  entryPagePath: string;
  referrerHost: string;
  utm: LeadAttributionContext["utm"];
}

interface TrackerOptions {
  storage?: () => Pick<Storage, "getItem" | "setItem" | "removeItem">;
  persistenceAllowed?: () => boolean;
  now?: () => number;
}

function safePath(value: string) {
  const printable = Array.from(value).filter((char) => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127).join("");
  const path = printable.split(/[?#]/, 1)[0].slice(0, MAX_PATH_LENGTH);
  return path.startsWith("/") ? path : UNKNOWN;
}

function safeLabel(value: string | null) {
  if (value && (!/^[A-Za-z0-9._ -]+$/.test(value) || /\d{7,}/.test(value))) return UNKNOWN;
  const label = (value ?? "")
    .replace(/[^A-Za-z0-9._ -]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_LABEL_LENGTH)
    .trim();
  return label || UNKNOWN;
}

function referrerHost(referrer: string) {
  if (!referrer) return UNKNOWN;
  try {
    const parsed = new URL(referrer);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return UNKNOWN;
    const host = parsed.hostname.toLowerCase().slice(0, MAX_HOST_LENGTH);
    return /^[a-z0-9.-]+$/.test(host) ? host : UNKNOWN;
  } catch {
    return UNKNOWN;
  }
}

function normalize(source: BrowserAttributionSource) {
  const params = new URLSearchParams(source.search);
  return {
    pagePath: safePath(source.pathname),
    referrerHost: referrerHost(source.referrer),
    utm: {
      source: safeLabel(params.get("utm_source")),
      medium: safeLabel(params.get("utm_medium")),
      campaign: safeLabel(params.get("utm_campaign")),
    },
  };
}

function browserSource(): BrowserAttributionSource {
  if (typeof window === "undefined") return { pathname: "", search: "", referrer: "" };
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    referrer: typeof document === "undefined" ? "" : document.referrer,
    origin: window.location.origin,
    navigationType: (window.performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined)?.type,
  };
}

function internalReferrer(source: BrowserAttributionSource) {
  try {
    return !!source.origin && new URL(source.referrer).origin === source.origin;
  } catch {
    return false;
  }
}

function parseSnapshot(raw: string | null, now: number): EntrySnapshot | null {
  if (!raw || raw.length > 2000) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || value.version !== 1 || !Number.isFinite(value.createdAt) ||
        value.createdAt < 0 || value.createdAt > now ||
        value.expiresAt !== value.createdAt + INQUIRY_SOURCE_TTL_MS ||
        typeof value.entryPagePath !== "string" ||
        typeof value.referrerHost !== "string" || !value.utm ||
        ["source", "medium", "campaign"].some((key) => typeof value.utm[key] !== "string")) return null;
    const path = safePath(value.entryPagePath);
    const host = value.referrerHost;
    if (path !== value.entryPagePath || host.length > MAX_HOST_LENGTH ||
        !/^[a-z0-9.-]+$/.test(host)) return null;
    // Copy only this schema's fields. Storage is untrusted and may contain
    // extra data, invalid labels or leftovers from a different implementation.
    return {
      version: 1,
      createdAt: value.createdAt,
      expiresAt: value.expiresAt,
      entryPagePath: path,
      referrerHost: host,
      utm: {
        source: safeLabel(value.utm.source),
        medium: safeLabel(value.utm.medium),
        campaign: safeLabel(value.utm.campaign),
      },
    };
  } catch {
    return null;
  }
}

/** Observed entry context; optional persistence is bounded, local and opt-in. */
export function createLeadAttributionTracker(
  readSource: () => BrowserAttributionSource = browserSource,
  options: TrackerOptions = {},
) {
  const now = options.now ?? (() => Date.now());
  const initialSource = readSource();
  const normalized = normalize(initialSource);
  const createdAt = now();
  const initial: EntrySnapshot = {
    version: 1, createdAt, expiresAt: createdAt + INQUIRY_SOURCE_TTL_MS,
    entryPagePath: normalized.pagePath,
    referrerHost: internalReferrer(initialSource) ? UNKNOWN : normalized.referrerHost,
    utm: internalReferrer(initialSource)
      ? { source: UNKNOWN, medium: UNKNOWN, campaign: UNKNOWN }
      : { ...normalized.utm },
  };
  let entry = initial;
  let previouslyAllowed = options.persistenceAllowed?.() === true;
  let persisted = false;
  const remove = () => {
    try { options.storage?.().removeItem(INQUIRY_SOURCE_KEY); } catch { /* Forms still work. */ }
    persisted = false;
  };
  if (previouslyAllowed) {
    try {
      const stored = parseSnapshot(options.storage?.().getItem(INQUIRY_SOURCE_KEY) ?? null, now());
      const continuation = initialSource.navigationType === "reload" ||
        initialSource.navigationType === "back_forward" || internalReferrer(initialSource);
      if (stored && continuation) entry = stored;
    } catch { /* Fall back to this document's observed context. */ }
  } else {
    remove();
  }

  const read = (): LeadAttributionContext => {
    const allowed = options.persistenceAllowed?.() === true;
    // Withdrawal removes restored context as well as the stored note. A form
    // may still send context visible on its own document, as it always did.
    if (previouslyAllowed && !allowed) entry = initial;
    previouslyAllowed = allowed;
    const expired = now() >= entry.expiresAt || now() < entry.createdAt;
    if (!allowed || expired) remove();
    else if (!persisted) {
      try {
        options.storage?.().setItem(INQUIRY_SOURCE_KEY, JSON.stringify(entry));
        persisted = true;
      } catch { /* Blocked storage must not prevent an inquiry. */ }
    }
    const current = normalize(readSource());
    return {
      entryPagePath: expired ? UNKNOWN : entry.entryPagePath,
      pagePath: current.pagePath,
      referrerHost: expired ? UNKNOWN : entry.referrerHost,
      utm: expired ? { source: UNKNOWN, medium: UNKNOWN, campaign: UNKNOWN } : { ...entry.utm },
    };
  };
  read(); // Capture an allowed entry before the visitor follows a document link.
  return read;
}

const readLeadAttribution = createLeadAttributionTracker(browserSource, {
  storage: () => window.sessionStorage,
  persistenceAllowed: inquirySourceAllowed,
});
const stopConsent = onConsentChange(() => { readLeadAttribution(); });
const storageChange = (event: StorageEvent) => {
  if (event.key === CONSENT_STORAGE_KEY || event.key === null) readLeadAttribution();
};
if (typeof window !== "undefined") window.addEventListener("storage", storageChange);
if (import.meta.hot) import.meta.hot.dispose(() => {
  stopConsent();
  window.removeEventListener("storage", storageChange);
});

export function leadAttributionContext() {
  return readLeadAttribution();
}
